import { useNavigate } from 'react-router-dom';
import { Compass, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex h-16 items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <Compass className="w-7 h-7 text-[#2563EB]" />
              <span className="font-bold text-xl tracking-tight font-['Manrope']">Klarvi</span>
            </button>
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="terms-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Volver
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-12 md:py-16" data-testid="terms-page">
        <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] font-['Manrope'] mb-2">
          Términos de Servicio
        </h1>
        <p className="text-sm text-muted-foreground mb-8">Klarvi — Fecha de última actualización: 30 de junio de 2026</p>

        <div className="prose prose-slate max-w-none space-y-8 text-[#0F172A] text-[15px] leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Aceptación de los términos</h2>
            <p>
              Al acceder o usar la plataforma Klarvi ("la Plataforma"), usted acepta quedar sujeto a estos Términos de Servicio.
              Si no está de acuerdo con alguno de estos términos, no debe utilizar la Plataforma.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Descripción del servicio</h2>
            <p>
              Klarvi ofrece herramientas informativas de orientación laboral, incluyendo un Asesor Laboral con inteligencia artificial,
              Calculadora de Liquidación, Evaluador de Oferta Laboral, Benchmark Salarial y Calculadora de Sueldo Neto.
            </p>
            <p className="mt-2">
              La información proporcionada por la Plataforma es solo para fines informativos y educativos, y no constituye
              asesoramiento legal, representación legal, asesoramiento fiscal ni asesoramiento financiero. Los resultados pueden
              variar según las circunstancias individuales, contratos y regulaciones locales. Para casos concretos, consulte con
              un abogado laboral u otro profesional calificado.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Cuenta de usuario</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Debe proporcionar información veraz y mantenerla actualizada al crear su cuenta.</li>
              <li>Es responsable de mantener la confidencialidad de sus credenciales de acceso.</li>
              <li>Debe notificarnos de inmediato ante cualquier uso no autorizado de su cuenta.</li>
              <li>Nos reservamos el derecho de suspender o eliminar cuentas que incumplan estos Términos.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Planes y pagos</h2>
            <p>
              Klarvi ofrece un plan gratuito con uso limitado y un plan Pro de pago (mensual o anual) con acceso ilimitado.
              Los precios vigentes se muestran en la página de precios de la Plataforma.
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Los pagos se procesan a través de Stripe y se cobran de forma recurrente según el ciclo elegido.</li>
              <li>Los pagos realizados no son reembolsables, salvo que la ley aplicable indique lo contrario.</li>
              <li>Puede cancelar su suscripción en cualquier momento desde su perfil; conservará el acceso Pro hasta el fin del periodo ya pagado.</li>
              <li>Nos reservamos el derecho de modificar los precios, notificándolo con anticipación razonable.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Uso aceptable</h2>
            <p>Al usar la Plataforma, usted se compromete a no:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Usar la Plataforma con fines ilegales o no autorizados.</li>
              <li>Intentar acceder sin autorización a sistemas, cuentas o datos de otros usuarios.</li>
              <li>Interferir con el funcionamiento normal de la Plataforma o sus servicios de IA.</li>
              <li>Revender, sublicenciar o explotar comercialmente el acceso a la Plataforma sin autorización expresa.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Programa de referidos</h2>
            <p>
              Klarvi puede ofrecer beneficios (descuentos o meses gratis) a usuarios que refieran nuevos suscriptores Pro. Estos
              beneficios están sujetos a las reglas vigentes descritas en la sección de referidos de su perfil y pueden modificarse
              o suspenderse en cualquier momento. Nos reservamos el derecho de anular beneficios obtenidos mediante fraude o abuso
              del programa.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Propiedad intelectual</h2>
            <p>
              El contenido, diseño, marca y software de Klarvi son propiedad de Klarvi o sus licenciantes y están protegidos por
              las leyes de propiedad intelectual aplicables. No se otorga ningún derecho de uso más allá del necesario para utilizar
              la Plataforma conforme a estos Términos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Limitación de responsabilidad</h2>
            <p>
              Klarvi se proporciona "tal cual" y "según disponibilidad". En la medida permitida por la ley, Klarvi no garantiza
              que el servicio sea ininterrumpido o libre de errores, y no será responsable por decisiones tomadas con base en la
              información proporcionada por la Plataforma, incluyendo las generadas por el Asesor Laboral con IA.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Terminación de cuenta</h2>
            <p>
              Puede eliminar su cuenta en cualquier momento desde la configuración de su perfil. Nos reservamos el derecho de
              suspender o terminar el acceso de cualquier usuario que incumpla estos Términos, sin perjuicio de otras acciones
              legales que correspondan.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Modificaciones a estos términos</h2>
            <p>
              Podemos actualizar estos Términos de Servicio periódicamente. Cualquier cambio relevante se notificará mediante la
              Plataforma. La versión vigente estará siempre disponible en{' '}
              <span className="font-medium">klarvi.org/terminos</span>. El uso continuado de la Plataforma tras una actualización
              constituye aceptación de los nuevos términos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Ley aplicable</h2>
            <p>
              Estos Términos se rigen por las leyes de los Estados Unidos Mexicanos. Cualquier controversia se someterá a los
              tribunales competentes, salvo que la ley aplicable disponga otra jurisdicción de forma imperativa.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold font-['Manrope'] mb-3">Contacto</h2>
            <p>
              Para preguntas sobre estos Términos, escríbanos a{' '}
              <a href="mailto:contacto@klarvi.org" className="text-[#2563EB] hover:underline">contacto@klarvi.org</a>.
            </p>
            <p className="mt-2">
              Klarvi es operado por PAYNE ADMINISTRATIVO, S.A. DE C.V. (RFC: PAD1905301TA), con domicilio en Calle Renato Leduc 174, Interior 301, Colonia Toriello Guerra, Alcaldía Tlalpan, C.P. 14050, Ciudad de México.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
